/**
 * SubCategoryDisplay.js
 * Written by: Alex Kasemir
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import debounce from 'lodash/debounce';

import SubCategoryColumn from 'components/SubCategoryColumn';

import userActions from 'store/user/actions';
import categoryActions from 'store/category/actions';
import categorySelectors from 'store/category/selectors';
import subcategorySelectors from 'store/subcategory/selectors';

import { reshape, SINGLE_COLUMN_BREAKPOINT } from 'utils';
import event from 'utils/event';
import { SUBCATEGORY_SELECTOR_BREAKPOINT } from 'root/theme';

import {
  SubCategoryDisplayContainer,
  CategoryHeadline,
  SpecialSubcategoryContainer,
  SubcategoryDifficultyContainer,
  Paginator,
  NextLink,
} from './SubCategoryDisplay.styles';
import LoadAble from '../../components/Loadable';


/**
 * View you submitted questions
 *
 */
export class SubCategoryDisplay extends Component {
  constructor(props) {
    super(props);
    this.state = {
      width: null,
    };
  }

  componentDidMount() {
    const { getCategory, selectedCategory, authedUserId, getUserStatsByCategory } = this.props;
    if (selectedCategory) {
      getCategory({ id: selectedCategory.id });
      if (authedUserId) {
        getUserStatsByCategory(authedUserId, selectedCategory.id);
      }
    }

    if (__CLIENT__) {
      this.updateWidth();
      window.addEventListener(`resize`, this.debouncedUpdateWidth);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      selectedCategory,
      getCategory,
      getUserStatsByCategory,
      authedUserId,
      statsByQuizLoaded,
      statsByQuizLoading,
    } = this.props;

    if ((!prevProps.selectedCategory && selectedCategory)
      || (selectedCategory && prevProps.selectedCategory.id !== selectedCategory.id)) {
      getCategory({ id: selectedCategory.id });
      if (authedUserId) {
        // statsByQuiz exists but user is reselecting a new category.
        getUserStatsByCategory(authedUserId, selectedCategory.id);
      }
    } else if (!statsByQuizLoaded && !statsByQuizLoading && (selectedCategory && authedUserId)) {
      // statsByQuiz have not loaded or loading, but selectedCategory exists and user is signed in.
      getUserStatsByCategory(authedUserId, selectedCategory.id);
    }
  }

  componentWillUnmount() {
    if (__CLIENT__) {
      window.removeEventListener(`resize`, this.debouncedUpdateWidth);
    }
  }

  getSpecialSubcategoryInfo = () => {
    const { specialSubcategories, selectedCategoryPages } = this.props;
    const { width } = this.state;

    const specialCols = width > SINGLE_COLUMN_BREAKPOINT
      ? reshape(specialSubcategories, 4)
      : [specialSubcategories];
    const pages = Math.ceil(specialCols.length / 2);

    return {
      pages,
      currentPage: selectedCategoryPages ? selectedCategoryPages.special : 1,
      cols: specialCols,
    };
  }

  getSubcategoryDifficultiesInfo = () => {
    const { subcategoryDifficulties, selectedCategoryPages } = this.props;
    const { width } = this.state;

    const subcategoryDifficultiesCols = width > SINGLE_COLUMN_BREAKPOINT
      ? reshape(subcategoryDifficulties, 3)
      : [subcategoryDifficulties];

    const pages = Math.ceil(subcategoryDifficultiesCols.length / 2);

    return {
      pages,
      currentPage: selectedCategoryPages ? selectedCategoryPages.emh : 1,
      cols: subcategoryDifficultiesCols,
    };
  }


  updateWidth = () => {
    this.setState({ width: window.innerWidth });
  }


  debouncedUpdateWidth = debounce(this.updateWidth, 200);


  changeEMHPage = (e) => {
    const { selectedCategoryPages, changePage, selectedCategory } = this.props;
    const value = e.target.name === `next` ? 1 : -1;

    event(`change-page`, [`click`, `change-page`, `${value === 1 ? `next` : `previous`}`, selectedCategory.id]);

    changePage(selectedCategory.id, `emh`, selectedCategoryPages.emh + value);
  }

  changeSpecialPage = (e) => {
    const { selectedCategoryPages, changePage, selectedCategory } = this.props;
    const value = e.target.name === `next` ? 1 : -1;

    event(`change-page`, [`click`, `change-page`, `${value === 1 ? `next` : `previous`}`, selectedCategory.id]);

    changePage(selectedCategory.id, `special`, selectedCategoryPages.special + value);
  }

  renderCategory = () => {
    const specialCols = this.getSpecialSubcategoryInfo();
    const subcategoryDifficultiesCols = this.getSubcategoryDifficultiesInfo();

    return (
      <>
        <SpecialSubcategoryContainer>
          {
            specialCols.cols.slice(
              (specialCols.currentPage * 2) - 2, specialCols.currentPage * 2)
              .map((col, idx) => (<SubCategoryColumn
                key={ idx }
                type="S"
                subcats={ col }
              />))
          }
          {
            specialCols.pages > 1
              ? <Paginator>
                { specialCols.currentPage > 1 ? <NextLink name="previous" onClick={ this.changeSpecialPage }> Previous </NextLink> : null }
                <div>{`${specialCols.currentPage} / ${specialCols.pages}`}</div>
                { specialCols.currentPage < specialCols.pages ? <NextLink name="next" onClick={ this.changeSpecialPage }> Next </NextLink> : null}
              </Paginator>
              : null
          }
        </SpecialSubcategoryContainer>
        <SubcategoryDifficultyContainer>
          {
            subcategoryDifficultiesCols.cols.slice(
              (subcategoryDifficultiesCols.currentPage * 2) - 2,
              subcategoryDifficultiesCols.currentPage * 2).map(
              (col, idx) => (<SubCategoryColumn
                key={ idx }
                type="EMH" subcats={ col }
              />))
          }
          {
            subcategoryDifficultiesCols.pages > 1
              ? <Paginator>
                { subcategoryDifficultiesCols.currentPage > 1 ? <NextLink name="previous" onClick={ this.changeEMHPage }> Previous </NextLink> : null }
                <div>{`${subcategoryDifficultiesCols.currentPage} / ${subcategoryDifficultiesCols.pages}`}</div>
                { subcategoryDifficultiesCols.currentPage < subcategoryDifficultiesCols.pages
                  ? <NextLink name="next" onClick={ this.changeEMHPage }> Next </NextLink>
                  : null
                }
              </Paginator>
              : null
          }
        </SubcategoryDifficultyContainer>
      </>
    );
  }

  render() {
    const { selectedCategory, isDesktop } = this.props;
    const { width } = this.state;

    const specialCols = this.getSpecialSubcategoryInfo();
    const subcategoryDifficultiesCols = this.getSubcategoryDifficultiesInfo();

    const firstClientRender = __CLIENT__ && width === null;

    return (
      <SubCategoryDisplayContainer>
        <CategoryHeadline>
          {
            (
              (firstClientRender && isDesktop)
              || width > SUBCATEGORY_SELECTOR_BREAKPOINT
              || (isDesktop && __SERVER__)
            )
              ? selectedCategory
                ? selectedCategory.name
                : null
              : null
          }
        </CategoryHeadline>
        <LoadAble
          ready={specialCols.pages !== 0 || subcategoryDifficultiesCols.pages !== 0}
          renderFunc={() => (this.renderCategory())}
        />
      </SubCategoryDisplayContainer>
    );
  }
}

SubCategoryDisplay.propTypes = {
  getCategory: PropTypes.func.isRequired,
  changePage: PropTypes.func.isRequired,
  selectedCategoryPages: PropTypes.object,
  selectedCategory: PropTypes.object,
  specialSubcategories: PropTypes.array,
  subcategoryDifficulties: PropTypes.array,
  authedUserId: PropTypes.number,
  getUserStatsByCategory: PropTypes.func,
  statsByQuizLoaded: PropTypes.bool,
  statsByQuizLoading: PropTypes.bool,
  isDesktop: PropTypes.bool,
};

export default connect((state) => {
  return {
    authedUserId: state.user.session.authedUserId,
    statsByQuizLoaded: state.user.session.statsByQuizLoaded,
    statsByQuizLoading: state.user.session.statsByQuizLoading,
    selectedCategory: categorySelectors.selected(state),
    selectedCategoryPages: categorySelectors.selectedCategoryPages(state),
    specialSubcategories: subcategorySelectors.selectedCategorySpecialSubs(state),
    subcategoryDifficulties: subcategorySelectors.selectedCategoryEMHSubs(state),
    isDesktop: state.extra.device.isDesktop,
  };
}, {
  getUserStatsByCategory: userActions.getUserStatsByCategory,
  getCategory: categoryActions.getCategory,
  changePage: categoryActions.changePage,
})(SubCategoryDisplay);
